import { Survey, SurveySort } from '../../models/survey.model';
import {
  SurveyStateFilters,
  SurveyRequestContext,
  SurveyState,
} from '../survey-state.model';
import { SurveyStateBuilder } from './survey-state.builder';

// ---------------------------------------------------------------------------
// SurveyStateManager
//
// Maps each NgRx action's payload to the corresponding state transition.
// Each method reads the current state (via SurveyStateBuilder), applies only
// the changes relevant to that action, and returns the new state.
// ---------------------------------------------------------------------------

export class SurveyStateManager {
  constructor(private readonly _state: SurveyState) {}

  /** Init: switch request context. Keeps filters, sort, and page-size intact. */
  public initialize(context: SurveyRequestContext): SurveyState {
    return SurveyStateBuilder.copy(this._state)
      .clearSurveys()
      .resetPage()
      .clearError()
      .setContext(context)
      .setLoading(true)
      .build();
  }

  /** SetFilters: merge partial overrides and restart from page 1. */
  public setFilters(filters: Partial<SurveyStateFilters>): SurveyState {
    return SurveyStateBuilder.copy(this._state)
      .clearSurveys()
      .resetPage()
      .clearError()
      .setFilters(filters)
      .setLoading(true)
      .build();
  }

  /** ResetFilters: restore filter defaults and restart from page 1. Preserves sort and context. */
  public resetFilters(): SurveyState {
    return SurveyStateBuilder.copy(this._state)
      .clearSurveys()
      .resetPage()
      .resetFilters()
      .clearError()
      .setLoading(true)
      .build();
  }

  /** SetSort: replace sort and restart from page 1. */
  public setSort(sort: SurveySort): SurveyState {
    return SurveyStateBuilder.copy(this._state)
      .clearSurveys()
      .resetPage()
      .clearError()
      .setSort(sort)
      .setLoading(true)
      .build();
  }

  /** Reload: re-fetch the current page without changing any state. */
  public reload(): SurveyState {
    return SurveyStateBuilder.copy(this._state)
      .clearError()
      .setLoading(true)
      .build();
  }

  /** LoadNextPage: advance to the next page for infinite scroll. */
  public loadNextPage(): SurveyState {
    return SurveyStateBuilder.copy(this._state)
      .setPagination({ page: this._state.pagination.page + 1 })
      .clearError()
      .setLoading(true)
      .build();
  }

  /** LoadSuccess: append the returned surveys and update hasMore. */
  public loadSuccess(surveys: Survey[]): SurveyState {
    return SurveyStateBuilder.copy(this._state)
      .setSurveys(surveys)
      .setPagination({
        hasMore: surveys.length === this._state.pagination.limit,
      })
      .clearError()
      .setLoading(false)
      .build();
  }

  /** LoadFailure: record the error and stop the loading indicator. */
  public loadFailure(error: string): SurveyState {
    return SurveyStateBuilder.copy(this._state)
      .setError(error)
      .setLoading(false)
      .build();
  }
}
